import { useCallback, useMemo } from 'react';

import {
  GROUP_SELECTED_ANNOTATIONS,
  SET_ANNOTATIONS_LOCK,
  UNGROUP_SELECTED_ANNOTATIONS,
} from '../actions';
import { WATERMARK_ANNOTATION_ID } from '../utils/constants';
import useStore from './useStore';

const useGroupLockActions = () => {
  const { dispatch, selectionsIds = [], annotations = {} } = useStore();

  const alignableSelectionIds = useMemo(
    () => selectionsIds.filter((id) => id !== WATERMARK_ANNOTATION_ID),
    [selectionsIds],
  );

  const canGroup = alignableSelectionIds.length >= 2;
  const canUngroup = alignableSelectionIds.some(
    (id) => annotations[id]?.groupId,
  );
  const canLock = alignableSelectionIds.some((id) => !annotations[id]?.locked);
  const canUnlock = alignableSelectionIds.some((id) => annotations[id]?.locked);

  const floatingLockAction = useMemo(() => {
    if (alignableSelectionIds.length === 0) {
      return null;
    }
    if (alignableSelectionIds.some((id) => annotations[id]?.locked)) {
      return 'unlock';
    }
    return 'lock';
  }, [alignableSelectionIds, annotations]);

  const floatingGroupAction = useMemo(() => {
    if (canUngroup) {
      return 'ungroup';
    }
    if (canGroup) {
      return 'group';
    }
    return null;
  }, [canGroup, canUngroup]);

  const group = useCallback(() => {
    if (!canGroup) {
      return false;
    }
    dispatch({
      type: GROUP_SELECTED_ANNOTATIONS,
      payload: { annotationIds: alignableSelectionIds },
    });
    return true;
  }, [alignableSelectionIds, canGroup, dispatch]);

  const ungroup = useCallback(() => {
    if (!canUngroup) {
      return false;
    }
    dispatch({
      type: UNGROUP_SELECTED_ANNOTATIONS,
      payload: { annotationIds: alignableSelectionIds },
    });
    return true;
  }, [alignableSelectionIds, canUngroup, dispatch]);

  const lock = useCallback(() => {
    if (!canLock) {
      return false;
    }
    dispatch({
      type: SET_ANNOTATIONS_LOCK,
      payload: {
        annotationIds: alignableSelectionIds.filter(
          (id) => !annotations[id]?.locked,
        ),
        locked: true,
      },
    });
    return true;
  }, [alignableSelectionIds, annotations, canLock, dispatch]);

  const unlock = useCallback(() => {
    if (!canUnlock) {
      return false;
    }
    dispatch({
      type: SET_ANNOTATIONS_LOCK,
      payload: {
        annotationIds: alignableSelectionIds.filter(
          (id) => annotations[id]?.locked,
        ),
        locked: false,
      },
    });
    return true;
  }, [alignableSelectionIds, annotations, canUnlock, dispatch]);

  return {
    group,
    ungroup,
    lock,
    unlock,
    canGroup,
    canUngroup,
    canLock,
    canUnlock,
    floatingLockAction,
    floatingGroupAction,
    hasSelection: alignableSelectionIds.length > 0,
  };
};

export default useGroupLockActions;
